/** Minimal gtag / dataLayer helpers for Google Ads (loaded only after marketing consent). */

import { allowsMarketingCookies } from "@/lib/cookieConsent";

/** Google Ads account / Google tag ID */
const AW_ID = "AW-7441707484";
/** Bilvärdering conversion action (send_to). */
const AW_BILVARDERING_SEND_TO = "AW-7441707484/dGU3CPzNrKAdENaf5ZoD";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let loadPromise: Promise<void> | null = null;
let consentDefaultSet = false;

function ensureDataLayer(): void {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    // Must push `arguments` (not a rest Array) — matches Google's snippet.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
}

/** Consent Mode defaults (denied) — call once before any gtag config/load. */
export function initGtagConsentDefaults(): void {
  if (typeof window === "undefined" || consentDefaultSet) return;
  consentDefaultSet = true;
  ensureDataLayer();
  window.gtag!("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  });
}

function grantMarketingConsent(): void {
  ensureDataLayer();
  initGtagConsentDefaults();
  window.gtag!("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "denied",
  });
}

function denyMarketingConsent(): void {
  ensureDataLayer();
  initGtagConsentDefaults();
  window.gtag!("consent", "update", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
}

/**
 * Load Google Ads gtag only when marketing consent is already granted.
 * Call grantMarketingConsent() before this.
 */
export function ensureGtagLoaded(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    ensureDataLayer();
    window.gtag!("js", new Date());
    window.gtag!("config", AW_ID);

    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-bilenia-gtag="${AW_ID}"]`,
    );
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${AW_ID}`;
    script.dataset.bileniaGtag = AW_ID;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });

  return loadPromise;
}

/** Apply consent to gtag if already loaded; load when granted. */
export function syncGtagWithMarketingConsent(granted: boolean): void {
  try {
    if (typeof window === "undefined") return;
    if (granted) {
      // Consent MUST be updated before config / script load
      grantMarketingConsent();
      void ensureGtagLoaded();
      return;
    }
    denyMarketingConsent();
  } catch {
    /* never block UX */
  }
}

export function trackGtagEvent(
  eventName: string,
  params: Record<string, unknown> = {},
): void {
  try {
    if (typeof window === "undefined") return;
    if (!allowsMarketingCookies()) return;

    grantMarketingConsent();
    void ensureGtagLoaded().then(() => {
      if (typeof window.gtag === "function") {
        window.gtag("event", eventName, params);
      }
    });
  } catch {
    // Never block UX on analytics failures
  }
}

export type BilvarderingConversionParams = {
  car_brand?: string;
  car_model?: string;
  [key: string]: unknown;
};

/** Google Ads conversion: successful car valuation (Bilvärdering). */
export function trackBilvarderingConversion(
  params: BilvarderingConversionParams = {},
): void {
  try {
    if (typeof window === "undefined") return;
    if (!allowsMarketingCookies()) return;

    grantMarketingConsent();
    void ensureGtagLoaded().then(() => {
      window.gtag?.("event", "conversion", {
        send_to: AW_BILVARDERING_SEND_TO,
        value: 1.0,
        currency: "SEK",
        car_brand: params.car_brand,
        car_model: params.car_model,
      });
    });
  } catch {
    // Never block UX on analytics failures
  }
}
